// client/src/components/pages/AmendmentsAllocateTransactions/FilterControls.tsx
// (converted from .js, ST-00221). The filter panel of the Amendments page.
import { useState, useEffect, type KeyboardEvent } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import DynamicFilters, {
  isDynamicFilterActive,
  knownDynamicFilters,
  type DynamicFilter,
} from './DynamicFilters';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import ThemedSelect from '../../common/ThemedSelect';
import { toApiDateString } from '../../../utils/dateUtils';
import type { AmendmentFilters } from '../../../types/clientPages/amendments';

export interface FilterControlsProps {
  onFilterChange: (filters: AmendmentFilters) => void;
  onClear?: () => void;
  isLoading: boolean;
  onCancelRequest?: () => void;
}

/** The text filters; each takes a comma-separated list of values. */
type TextFilterName =
  | 'dispatchLocation'
  | 'receiptLocation'
  | 'registration'
  | 'ticketNo'
  | 'deliveryNo'
  | 'refNo'
  | 'ticketType'
  | 'orderNo'
  | 'product'
  | 'haulier'
  | 'tag1'
  | 'tag2'
  | 'tag3'
  | 'tag4';

type FilterState = Record<TextFilterName, string> & {
  startDate: string;
  endDate: string;
  minNettWeight: string;
  filterType: string;
};

interface FilterField {
  name: TextFilterName;
  label: string;
}

const SAVED_DYNAMIC_FILTERS_KEY = 'amendmentsDynamicFilters';

/** The saved dynamic filters, without any on a column that is no longer offered (AMD-24). */
const loadSavedDynamicFilters = (): DynamicFilter[] => {
  try {
    const saved = localStorage.getItem(SAVED_DYNAMIC_FILTERS_KEY);
    return saved ? knownDynamicFilters(JSON.parse(saved)) : [];
  } catch (error) {
    console.warn('Failed to load saved dynamic filters:', error);
    return [];
  }
};

// Default dates: this month up to yesterday; on the 1st of the month, the full previous month.
const getDefaultStartDate = () => {
  const now = new Date();
  if (now.getDate() === 1) {
    return toApiDateString(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  }
  return toApiDateString(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getDefaultEndDate = () => {
  const now = new Date();
  if (now.getDate() === 1) {
    return toApiDateString(new Date(now.getFullYear(), now.getMonth(), 0)); // Last day of previous month
  }
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toApiDateString(yesterday);
};

const emptyFilters = (): FilterState => ({
  startDate: getDefaultStartDate(),
  endDate: getDefaultEndDate(),
  dispatchLocation: '',
  receiptLocation: '',
  registration: '',
  ticketNo: '',
  deliveryNo: '',
  refNo: '',
  ticketType: '',
  orderNo: '',
  product: '',
  haulier: '',
  tag1: '',
  tag2: '',
  tag3: '',
  tag4: '',
  minNettWeight: '',
  filterType: 'Contains',
});

const filterTypes = ['Contains', 'StartsWith', 'EndsWith', 'Equals'];

// Basic filter fields (always visible)
const basicFilterFields: FilterField[] = [
  { name: 'dispatchLocation', label: 'Dispatch Location' },
  { name: 'receiptLocation', label: 'Receipt Location' },
  { name: 'registration', label: 'Registration' },
  { name: 'ticketNo', label: 'Ticket No' },
];

// Additional filter fields (shown in collapsible section)
const additionalFilterFields: FilterField[] = [
  { name: 'deliveryNo', label: 'Delivery No' },
  { name: 'refNo', label: 'Ref No' },
  { name: 'ticketType', label: 'Ticket Type' },
  { name: 'orderNo', label: 'Order No' },
  { name: 'product', label: 'Product' },
  { name: 'haulier', label: 'Haulier' },
  { name: 'tag1', label: 'Tag 1' },
  { name: 'tag2', label: 'Tag 2' },
  { name: 'tag3', label: 'Tag 3' },
  { name: 'tag4', label: 'Tag 4' },
];

// Combined list for processing
const filterFields: FilterField[] = [...basicFilterFields, ...additionalFilterFields];

const FilterControls = ({
  onFilterChange,
  onClear,
  isLoading,
  onCancelRequest,
}: FilterControlsProps) => {
  const [filters, setFilters] = useState<FilterState>(emptyFilters);

  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>(loadSavedDynamicFilters);
  const [applyError, setApplyError] = useState('');

  // Save dynamic filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SAVED_DYNAMIC_FILTERS_KEY, JSON.stringify(dynamicFilters));
    } catch (_) {
      /* storage unavailable: the filters still apply, they are just not remembered */
    }
  }, [dynamicFilters]);

  const handleChange = (e: { target: { name?: string; value: unknown } }) => {
    const { name, value } = e.target;
    if (!name) return;
    setFilters((prev) => ({ ...prev, [name]: String(value) }));
    if (applyError) setApplyError('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleApplyFilters();
    }
  };

  const handleApplyFilters = () => {
    const hasTextFilter = filterFields.some(
      (field) => filters[field.name] && filters[field.name].trim() !== ''
    );
    const hasWeightFilter = !!filters.minNettWeight && !isNaN(parseFloat(filters.minNettWeight));
    // "Is Empty" / "Is Not Empty" have no value and count as well (AMD-24)
    const activeDynamicFilters = dynamicFilters.filter(isDynamicFilterActive);
    const hasActiveDynamicFilters = activeDynamicFilters.length > 0;

    // Date-only searches are allowed; the date range itself bounds the query.
    // Guard only against fully unbounded searches (no dates AND no other filters).
    const hasDateBound =
      (filters.startDate && filters.startDate.trim() !== '') ||
      (filters.endDate && filters.endDate.trim() !== '');
    if (!hasDateBound && !hasTextFilter && !hasWeightFilter && !hasActiveDynamicFilters) {
      setApplyError('Please enter a date range or at least one filter criterion before searching.');
      return;
    }
    setApplyError('');

    const apiFilters: AmendmentFilters = {
      dateRange: {
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
      },
      filterType: filters.filterType,
    };

    filterFields.forEach((field) => {
      if (filters[field.name]) {
        apiFilters[field.name] = filters[field.name]
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');
      }
    });

    if (hasWeightFilter) {
      apiFilters.minNettWeight = parseFloat(filters.minNettWeight);
    }

    if (hasActiveDynamicFilters) {
      const dynamicFilterData: NonNullable<AmendmentFilters['dynamicFilters']> = {};
      activeDynamicFilters.forEach((filter) => {
        dynamicFilterData[filter.column] = {
          // Sent with '' when the operator takes no value; the server reads IS NULL / = ''
          value: filter.value ?? '',
          operator: filter.operator,
          type: filter.type,
        };
      });
      apiFilters.dynamicFilters = dynamicFilterData;
    }

    onFilterChange(apiFilters);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters());
    setDynamicFilters([]);
    setApplyError('');
    try {
      localStorage.removeItem(SAVED_DYNAMIC_FILTERS_KEY);
    } catch (_) {
      /* storage unavailable */
    }

    if (onClear) {
      onClear();
    }
  };

  const toggleAdditionalFilters = () => {
    setShowAdditionalFilters(!showAdditionalFilters);
  };

  // Dynamic filter handlers
  const handleAddDynamicFilter = (filter: DynamicFilter) => {
    setDynamicFilters((prev) => [...prev, filter]);
  };

  const handleRemoveDynamicFilter = (filterId: number) => {
    setDynamicFilters((prev) => prev.filter((filter) => filter.id !== filterId));
  };

  const handleDynamicFilterChange = (
    filterId: number,
    field: 'operator' | 'value',
    value: string
  ) => {
    setDynamicFilters((prev) =>
      prev.map((filter) => (filter.id === filterId ? { ...filter, [field]: value } : filter))
    );
  };

  return (
    <div className="bg-surface border border-slate-200 rounded-lg shadow-sm">
      <div className="p-4 space-y-3">
        {/* All filters + actions in a single flowing flex row */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Date Range Picker - constrained natural width */}
          <div className="w-full sm:w-auto sm:min-w-[270px] sm:max-w-[320px]">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Date Range
            </label>
            <UnifiedDateRangePicker
              startDate={filters.startDate}
              onStartDateChange={(date) =>
                setFilters((prev) => ({ ...prev, startDate: toApiDateString(date) }))
              }
              endDate={filters.endDate}
              onEndDateChange={(date) =>
                setFilters((prev) => ({ ...prev, endDate: toApiDateString(date) }))
              }
              disabled={isLoading}
            />
          </div>

          {/* Match Type */}
          <div className="min-w-[110px] max-w-[150px] flex-shrink-0">
            <label
              htmlFor="filterType"
              className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1"
            >
              Match Type
            </label>
            <ThemedSelect
              id="filterType"
              name="filterType"
              value={filters.filterType}
              onChange={handleChange}
              className="block w-full px-2.5 py-[7px] text-sm border border-slate-300 bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              {filterTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </ThemedSelect>
          </div>

          {/* Basic filter fields - all flow naturally */}
          {basicFilterFields.map((field) => (
            <div key={field.name} className="flex-1 min-w-[130px] max-w-[200px]">
              <label
                htmlFor={field.name}
                className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1"
              >
                {field.label}
              </label>
              <input
                type="text"
                id={field.name}
                name={field.name}
                value={filters[field.name]}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="block w-full px-2.5 py-[7px] text-sm border border-slate-300 bg-surface dark:bg-surface-sunken text-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-slate-400"
                placeholder={field.label}
              />
            </div>
          ))}

          {/* Action buttons - stay together, push right */}
          <div className="flex items-end gap-2 ml-auto">
            <button
              onClick={toggleAdditionalFilters}
              className="inline-flex items-center gap-1 px-2.5 py-[7px] text-xs font-medium text-slate-600 bg-surface border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
            >
              {showAdditionalFilters ? (
                <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {showAdditionalFilters ? 'Less' : 'More'}
            </button>
            <button
              onClick={handleClearFilters}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-[7px] text-xs font-medium text-slate-600 bg-surface border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleApplyFilters}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-4 py-[7px] text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Applying...
                </>
              ) : (
                <>
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  Apply
                </>
              )}
            </button>
          </div>
        </div>

        {/* Validation message */}
        {applyError && (
          <p className="text-xs text-amber-600 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {applyError}
          </p>
        )}

        {/* Additional Filters - Collapsible */}
        {showAdditionalFilters && (
          <div className="pt-3 border-t border-slate-200">
            <div className="flex flex-wrap gap-3">
              {additionalFilterFields.map((field) => (
                <div key={field.name} className="flex-1 min-w-[130px] max-w-[200px]">
                  <label
                    htmlFor={field.name}
                    className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1"
                  >
                    {field.label}
                  </label>
                  <input
                    type="text"
                    id={field.name}
                    name={field.name}
                    value={filters[field.name]}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className="block w-full px-2.5 py-[7px] text-sm border border-slate-300 bg-surface dark:bg-surface-sunken text-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-slate-400"
                    placeholder={
                      field.name.startsWith('tag') ? `${field.label} or NULL` : field.label
                    }
                  />
                </div>
              ))}
              <div className="flex-1 min-w-[130px] max-w-[200px]">
                <label
                  htmlFor="minNettWeight"
                  className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1"
                >
                  Min Nett Wgt
                </label>
                <input
                  type="number"
                  id="minNettWeight"
                  name="minNettWeight"
                  value={filters.minNettWeight}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  className="block w-full px-2.5 py-[7px] text-sm border border-slate-300 bg-surface dark:bg-surface-sunken text-slate-800 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-slate-400"
                  placeholder="e.g., 5"
                  min="0"
                />
              </div>
            </div>

            {/* Dynamic Filters Section */}
            <div className="mt-3">
              <DynamicFilters
                dynamicFilters={dynamicFilters}
                onDynamicFilterChange={handleDynamicFilterChange}
                onRemoveDynamicFilter={handleRemoveDynamicFilter}
                onAddDynamicFilter={handleAddDynamicFilter}
              />
            </div>
          </div>
        )}
      </div>

      {/* Loading indicator - slim bar at bottom */}
      {isLoading && (
        <div className="border-t border-slate-200">
          <div className="flex items-center justify-between px-4 py-2 bg-primary-50/50">
            <div className="flex items-center gap-2">
              <svg className="animate-spin h-3.5 w-3.5 text-primary-600" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-xs font-medium text-primary-700">Loading data...</span>
            </div>
            <button
              onClick={onCancelRequest}
              className="text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-0.5 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
          <div className="h-0.5 bg-primary-100">
            <div className="h-0.5 bg-primary-500 animate-pulse" style={{ width: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;
